import { TableV3, TableV3Ref, TableV3Sort } from 'common/table';
import { Header } from 'common/ui/Header';
import React, { useEffect, useRef, useState } from 'react';
import { mapSourcingInternalAnalysisSelectionDataToTableColumns } from 'modules/sourcing-internal/tables/maps';
import { sourcingInternalAnalysisSelectionColumns } from 'modules/sourcing-internal/tables/columns';
import {
  SearchableCandidateBatchSortableFields,
  SearchableSortDirection,
  useSearchCandidateBatchesQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { CandidateBatchStatus } from 'generated-graphql/types';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import { Button } from 'system/atoms';
import { useSourcingInternalStore } from 'modules/sourcing-internal/stores/hooks/useSourcingInternalStore';
import { useShallow } from 'zustand/react/shallow';
import { useSearchCandidateProfileSelected } from 'modules/candidate-profile/hooks/useSearchCandidateProfileSelected';
import { SourcingInternalAnalysisSelectionTableData } from 'modules/sourcing-internal/tables/types/SourcingInternalAnalysisSelectionTableData';

export const SourcingInternalAnalysisSelectionTable: React.FC = () => {
  const navigate = useNavigate();
  const organization = useOrganization();
  const tableRef =
    useRef<TableV3Ref<SourcingInternalAnalysisSelectionTableData>>(null);
  const hasRestoredSelectionRef = useRef(false);
  const [sort, setSort] = useState<
    TableV3Sort<SearchableCandidateBatchSortableFields>
  >({
    field: SearchableCandidateBatchSortableFields.UpdatedAt,
    direction: SearchableSortDirection.Desc
  });
  const { sessionId, candidateBatch, onSelectAnalysis } =
    useSourcingInternalStore(
      useShallow((state) => ({
        sessionId: state.sessionId,
        candidateBatch: state.candidateBatch,
        onSelectAnalysis: state.onSelectAnalysis
      }))
    );

  const { selectedCandidateIds } = useSearchCandidateProfileSelected({
    sessionId,
    tableRef: undefined
  });

  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(
    candidateBatch?.id ?? null
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data, loading, refetch } = useSearchCandidateBatchesQuery({
    variables: {
      filter: {
        organizationId: {
          eq: organization.id
        },
        status: {
          ne: CandidateBatchStatus.Draft
        }
      },
      limit: pageSize,
      from: currentPage * pageSize,
      sort: {
        field: sort.field,
        direction: sort.direction as SearchableSortDirection
      }
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleChangePage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const analyses = extractQueryListItems(data);
  const total = data?.searchCandidateBatches?.total ?? 0;

  const mappedAnalyses =
    mapSourcingInternalAnalysisSelectionDataToTableColumns(analyses);

  // Get total candidates from session
  const totalCandidates = selectedCandidateIds.length;

  // Reset the restoration flag when data starts loading (reload)
  useEffect(() => {
    if (loading) {
      hasRestoredSelectionRef.current = false;
    }
  }, [loading]);

  // Restore selection once after data loads if we have a candidate batch from the store
  useEffect(() => {
    if (
      !loading &&
      candidateBatch &&
      tableRef.current &&
      !hasRestoredSelectionRef.current
    ) {
      // Restore the visual selection but ensure it doesn't block user interactions
      tableRef.current.select([candidateBatch.id]);
      // Also update the local state so the button is enabled
      setSelectedAnalysisId(candidateBatch.id);

      hasRestoredSelectionRef.current = true;
    }
  }, [loading, candidateBatch]);

  const handleSelectionChanged = (selectedIds: string[]) => {
    const selectedId = selectedIds[0];
    if (!selectedId) {
      setSelectedAnalysisId(null);
      return;
    }

    const selectedAnalysis = mappedAnalyses.find(
      (analysis) => analysis.id === selectedId
    );
    if (!selectedAnalysis) return;

    const selectedBatch = analyses.find((batch) => batch.id === selectedId);
    if (selectedBatch) {
      setSelectedAnalysisId(selectedBatch.id);
    }
  };

  return (
    <>
      <Header
        title="Choose analysis"
        subtitle={`Invite ${totalCandidates} candidate${
          totalCandidates !== 1 ? 's' : ''
        } to apply for job.`}
        breadcrumbs={[
          {
            name: 'Candidates',
            action: handleBack
          }
        ]}
        onBack={handleBack}
        currentBreadcrumb="Invite to apply"
        actions={
          <Stack direction="row" gap="14px" alignItems="center">
            <Button variant="tertiary" onClick={handleRefresh}>
              Refresh
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (!selectedAnalysisId) return;
                onSelectAnalysis(selectedAnalysisId);
              }}
              disabled={!selectedAnalysisId}
            >
              Confirm and continue
            </Button>
          </Stack>
        }
      />
      <TableV3
        ref={tableRef}
        tableId="sourcing-internal-analysis-selection"
        columns={sourcingInternalAnalysisSelectionColumns}
        data={mappedAnalyses}
        selectionOptions={{
          mode: 'singleRow',
          identifier: 'id',
          onSelectionChanged: handleSelectionChanged
        }}
        loading={loading}
        defaultSort={sort}
        onSort={setSort}
        paginationOptions={{
          pageSize,
          totalRows: total,
          currentPage,
          onChangePage: handleChangePage,
          onChangePageSize: handleChangePageSize
        }}
      />
    </>
  );
};
